import { cssBundleHref } from '@remix-run/css-bundle';
import {
  json,
  type DataFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type V2_MetaFunction } from
'@remix-run/node';
import {
  Form,
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useSubmit } from
'@remix-run/react';
import { withSentry } from '@sentry/remix';
import { getTheme } from './routes/resources+/theme/theme-session.server.ts';
import fontStylestylesheetUrl from './styles/font.css';
import tailwindStylesheetUrl from './styles/tailwind.css';
import { authenticator, getUserId } from './utils/auth.server.ts';
import { ClientHintCheck, getHints } from './utils/client-hints.tsx';
import { prisma } from './utils/db.server.ts';
import { getEnv } from './utils/env.server.ts';
import { combineHeaders, getDomainUrl, getUserImgSrc } from './utils/misc.ts';
import { useNonce } from './utils/nonce-provider.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { useOptionalUser, useUser } from './utils/user.ts';
import { useRef } from 'react';
import { Button } from './components/ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger } from
'./components/ui/dropdown-menu.tsx';
import { Icon, href as iconsHref } from './components/ui/icon.tsx';
import { Confetti } from './components/confetti.tsx';
import { getFlashSession } from './utils/flash-session.server.ts';
import { useToast } from './utils/useToast.tsx';
import { Toaster } from './components/ui/toaster.tsx';

const homeIcon = '/img/home-icon.svg';

export const links: LinksFunction = () => {
  return [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: 'preload', href: iconsHref, as: 'image' },
  // Preload CSS as a resource to avoid render blocking
  { rel: 'preload', href: fontStylestylesheetUrl, as: 'style' },
  { rel: 'preload', href: tailwindStylesheetUrl, as: 'style' },
  cssBundleHref ? { rel: 'preload', href: cssBundleHref, as: 'style' } : null,
  { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
  {
    rel: 'alternate icon',
    type: 'image/png',
    href: '/favicons/favicon-32x32.png'
  },
  { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
  { rel: 'manifest', href: '/site.webmanifest' },
  { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
  { rel: 'stylesheet', href: fontStylestylesheetUrl },
  { rel: 'stylesheet', href: tailwindStylesheetUrl },
  cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null].
  filter(Boolean);
};

export const meta: V2_MetaFunction = () => {
  return [
  { title: 'Career Chat' },
  { name: 'description', content: 'AI Powered Interview Preparation' }];

};

export async function loader({ request }: DataFunctionArgs) {
  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root'
  });

  const user = userId ?
  await time(
    () =>
    prisma.user.findUnique({
      where: { id: userId },
      select: {
        id: true,
        name: true,
        username: true,
        imageId: true,
        roles: true
      }
    }),
    { timings, type: 'find user', desc: 'find user in root' }
  ) :
  null;
  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await authenticator.logout(request, { redirectTo: '/' });
  }
  const { flash, headers: flasHeaders } = await getFlashSession(request);

  return json(
    {
      user,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        session: {
          theme: await getTheme(request)
        }
      },
      ENV: getEnv(),
      flash
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        flasHeaders
      )
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? ''
  };
  return headers;
};

function App() {
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const user = useOptionalUser();
  useToast(data.flash?.toast);

  return (
    // Theme can be dynamic with `${theme}`
    <html lang="en" className="light h-full">
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
				<link rel="preload" as="image" href={homeIcon}></link>
				<link rel="preload" as="image" href="/img/interviewer.webp"></link>
			</head>
			<body className="flex h-full flex-col justify-between bg-background font-inter text-foreground">
				<header className="m-0 border-b px-14 py-2">
					<nav className="flex items-center justify-between">
						<Link to={user ? '/dashboard' : '/'}>
							<div className="flex flex-row items-end">
								<img src={homeIcon} alt="Home Icon" />
								<div className="ml-3 w-full text-lg font-bold tracking-widest text-[#2D7465]">
									Career Chat
								</div>
							</div>
						</Link>
						{user &&
            <div>
								<span className="rounded-full bg-blue-500 px-10 py-1 text-white shadow-xl">
									Beta
								</span>
							</div>}

						<div className="flex items-center gap-10">
							{user ?
              <>
									<a
                  href="https://forms.gle/QWPLBAn61TrQLPZK8"
                  target="_blank"
                  rel="noopener noreferrer">

										<Button className="ml-5 border bg-gray-200 text-gray-700 hover:bg-gray-300">
											Send Feedback
										</Button>
									</a>
									<UserDropdown />
								</> :

              <Button
                asChild
                variant="default"
                size="sm"
                className="ml-5 border border-[#3D8173] bg-[#3D8173] hover:bg-[#3D8173]/90">

									<Link to="/login">Log In</Link>
								</Button>}

						</div>
					</nav>
				</header>

				<div className="min-h-[400px] flex-1 overflow-auto">
					<Outlet />
				</div>
				<footer className="flex justify-end border-t px-14 py-4">
					<Link to="/privacy-policy">Privacy Policy</Link>
				</footer>
				<Confetti confetti={data.flash?.confetti} />
				<Toaster />
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data.ENV)}`
          }} />

				<LiveReload nonce={nonce} />
			</body>
		</html>);

}
export default withSentry(App);

function UserDropdown() {
  const user = useUser();
  const submit = useSubmit();
  const formRef = useRef<HTMLFormElement>(null);
  return (
    <DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary" className="px-5 py-6 shadow-md">
					<Link
            to={`/users/${user.username}`}
            // this is for progressive enhancement
            onClick={(e) => e.preventDefault()}
            className="flex items-center">

						<img
              className="h-8 w-8 rounded-full object-cover"
              alt={user.name ?? user.username}
              src={getUserImgSrc(user.imageId)} />

						<span className="text-body-sm font-bold">
							{user.name ?? user.username}
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}`}>
							<Icon className="text-body-md" name="avatar">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					{user.roles.map((role) => role.name).includes('admin') &&
          <DropdownMenuItem asChild>
							<Link prefetch="intent" to="/admin">
								<Icon className="text-body-md" name="pencil-2">
									Admin
								</Icon>
							</Link>
						</DropdownMenuItem>}

					<DropdownMenuItem
            asChild
            // this prevents the menu from closing before the form submission is completed
            onSelect={(event) => {
              event.preventDefault();
              submit(formRef.current);
            }}>

						<Form action="/logout" method="POST" ref={formRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>);

}